import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { DeesElement, customElement, html, state, cssManager } from '@design.estate/dees-element';
import { demoPage } from '../../../demos/templates.js';
import { additionalMetrics, healthScenarios, operationMetrics, operationStatus, operationsDemoStyles, type TDemoHealth } from '../../../demos/operations.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import '../../00group-button/dees-button/dees-button.js';
import '../dees-dataview-statusobject/dees-dataview-statusobject.js';
import type { IStatsTile } from './dees-statsgrid.js';

@customElement('dees-demo-operations-overview')
class DeesDemoOperationsOverview extends DeesElement {
  @state() private accessor scenario: TDemoHealth = 'healthy';
  @state() private accessor step = 0;
  @state() private accessor sampledAt = Date.now();
  @state() private accessor message = 'Choose a scenario or advance the sample to see the service change.';
  @state() private accessor showTypes = false;
  public static styles = [themeDefaultStyles, cssManager.defaultStyles, operationsDemoStyles];

  private choose(scenario: TDemoHealth) {
    this.scenario = scenario; this.step = 0; this.sampledAt = Date.now();
    this.message = `${healthScenarios.find((entry) => entry.key === scenario)!.label} scenario loaded.`;
  }

  private advance() {
    this.step++; this.sampledAt = Date.now();
    this.message = `Sample ${this.step + 1} received. Metrics and service checks are up to date.`;
  }

  private async showMetric(tile: IStatsTile) {
    await DeesModal.createAndShow({
      heading: tile.title, subheading: 'API gateway · current sample', width: 'small',
      content: html`<p style="font-size:32px;font-weight:600;letter-spacing:-.025em;margin:0 0 12px">${tile.value} <span style="font-size:14px;font-weight:400">${tile.unit}</span></p>
        <p style="font-size:13px;color:var(--dees-color-text-secondary);margin:0">${tile.description}</p>`,
      menuOptions: [{ name: 'Done', action: (modal) => modal!.destroy() }],
    });
  }

  private get metrics() {
    return operationMetrics(this.scenario, this.step).map((tile): IStatsTile => ({
      ...tile,
      actions: tile.id === 'requests' ? [
        { name: 'View request details', iconName: 'lucide:ArrowUpRight', action: async () => this.showMetric(tile) },
        { name: 'Advance sample', iconName: 'lucide:RefreshCw', action: async () => this.advance() },
      ] : tile.id === 'latency' ? [{ name: 'View response time', iconName: 'lucide:ArrowUpRight', action: async () => this.showMetric(tile) }] : undefined,
    }));
  }

  public render() {
    return html`
      <div class="toolbar">
        <dees-input-multitoggle label="Service scenario" .labelPosition=${'none'}
          .options=${healthScenarios.map(item => ({ key: item.key, label: item.label }))} .selectedOption=${this.scenario}
          @change=${(event: CustomEvent) => this.choose(event.detail.value)}></dees-input-multitoggle>
        <dees-button type="ghost" text="Reset overview" @clicked=${() => this.choose('healthy')}></dees-button>
      </div>
      <section class="section" aria-label="Gateway metrics">
        <div class="section-heading"><h2>Gateway overview</h2><span class="caption">Last 5 minutes · sample ${this.step + 1}</span></div>
        <dees-statsgrid .tiles=${this.metrics} .minTileWidth=${200} .gap=${12}></dees-statsgrid>
      </section>
      <dees-dataview-statusobject .statusObject=${operationStatus(this.scenario, this.step, this.sampledAt)}></dees-dataview-statusobject>
      <div class="foot"><p class="feedback" role="status">${this.message}</p>
        <dees-button text="Advance sample" type="outline" icon="lucide:RefreshCw" @clicked=${this.advance}></dees-button></div>
      <details @toggle=${(event: Event) => { this.showTypes = (event.currentTarget as HTMLDetailsElement).open; }}>
        <summary><dees-icon icon="lucide:ChevronRight" iconSize="16" aria-hidden="true"></dees-icon>Explore metric types</summary>
        ${this.showTypes ? html`<div class="examples"><p class="caption">State, regional capacity, CPU cores, volumes, and drives. Wider metrics adapt to the available columns.</p>
          <dees-statsgrid .tiles=${additionalMetrics()} .minTileWidth=${260} .gap=${12}
            .gridActions=${[{ name: 'Refresh hardware sample', iconName: 'lucide:RefreshCw', action: async () => { this.message = 'Hardware checks completed. Capacity and drive health are unchanged.'; } }]}></dees-statsgrid>
        </div>` : ''}
      </details>`;
  }
}

export const demoFunc = () => demoPage('Service overview', 'Follow traffic, capacity, and health in one place. Open a metric for detail, or explore how a service recovers.', html`<dees-demo-operations-overview></dees-demo-operations-overview>`);
